// The public certificate check behind a certificate's link, /verify/<number>/<token> (Adam, 3 Oct:
// the number and the certificate's random check token together). It calls certificate-check, 1 of
// the 3 functions that answer without a login (docs/data-model.md 10.3), so it works logged out and
// logged in alike. The token goes in the request body, never back into an address.
import { supabase } from './supabase.js'

// The function's own not-found line (supabase/functions/certificate-check/logic.ts), used here too
// for a link with no token, which isn't sent at all
export const NOT_FOUND_LINE = "We can't find a certificate for that link. Check it's the whole link from the certificate. If it still doesn't work, email adam@adamslee.co.uk."
const NOT_LOADED_LINE = "That didn't load. Check your connection and try again."

// the same shapes the function reads: numbers like FE-3-0042, tokens of 16 to 64 letters and digits
const NUMBER = /^[A-Za-z0-9-]{3,32}$/
const TOKEN = /^[A-Za-z0-9]{16,64}$/

/** /verify/FE-3-0042/<token> as { number, token }, or null when the link isn't whole. */
export function parseVerifyPath(path) {
  const parts = String(path || '').split('?')[0].split('/').filter(Boolean)
  if (parts[0] !== 'verify' || parts.length !== 3) return null
  let number
  let token
  try {
    number = decodeURIComponent(parts[1]).trim()
    token = decodeURIComponent(parts[2]).trim()
  } catch {
    return null
  }
  if (!NUMBER.test(number) || !TOKEN.test(token)) return null
  return { number: number.toUpperCase(), token: token.toLowerCase() }
}

/**
 * { status: 'found', certificate } with number, name, course, cohort, period_from, period_to,
 * completed_on and issuer; or { status: 'not_found' | 'limited' | 'error', message }.
 */
export async function checkCertificate(number, token) {
  try {
    const { data, error } = await supabase.functions.invoke('certificate-check', { body: { number, token } })
    if (!error && data?.found && data.certificate) return { status: 'found', certificate: data.certificate }
    const res = error?.context
    if (res && typeof res.json === 'function') {
      const answer = await res.json().catch(() => null)
      if (res.status === 404) return { status: 'not_found', message: answer?.message || NOT_FOUND_LINE }
      if (res.status === 429) return { status: 'limited', message: answer?.message || NOT_LOADED_LINE }
    }
    return { status: 'error', message: NOT_LOADED_LINE }
  } catch {
    return { status: 'error', message: NOT_LOADED_LINE }
  }
}

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September',
  'October', 'November', 'December']

/** A date column (YYYY-MM-DD) as "14 October 2026", or without its year. */
export function certificateDate(iso, withYear = true) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '')
  if (!m) return ''
  const day = `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]}`
  return withYear ? `${day} ${m[1]}` : day
}

/** "Cohort 3, 9 September to 14 October 2026", or the cohort alone, or null with no intake. */
export function cohortLine(c) {
  if (!c?.cohort) return null
  if (c.period_from && c.period_to) {
    const sameYear = c.period_from.slice(0, 4) === c.period_to.slice(0, 4)
    return `${c.cohort}, ${certificateDate(c.period_from, !sameYear)} to ${certificateDate(c.period_to)}`
  }
  return c.cohort
}
